/* eslint-disable react/require-default-props */

import React, {
    ComponentType,
    HTMLAttributes,
    useState,
} from 'react';
import {noop} from '@pluto/utils';
import Autocomplete, {AutocompleteRenderInputParams} from '@material-ui/lab/Autocomplete';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import {curry} from 'ramda';
import {ReactSelectOpt} from '@pluto/select';
import {BlankChangeEvent} from '@pluto/@types';
import humanizeRange from '@pluto/humanize-moment-range';
import List from './AutocompleteListContainer';

import './Autocomplete.scss';

export type StringWithOptionArray = string[] | ReactSelectOpt[];
export type StringWithOptionValue = string | ReactSelectOpt;

interface AutocompleteContainerProps {
    placeholder: string;
    values: string[];
    onChange: () => void;
    onInputChangeCallback?: (value: any) => void;
    options: ReactSelectOpt[];
    isLoading: boolean;
    limitTags?: number,
    variant?: string,
}

export default function AutocompleteContainer(props: AutocompleteContainerProps) {
    const {
        placeholder,
        values,
        onChange,
        options,
        isLoading,
        limitTags = 1,
        onInputChangeCallback = noop,
        variant = 'standard',
    } = props;
    const [inputValue, setInputValue] = useState<string>('');

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <Autocomplete
            className="autocomplete-overwrite"
            value={values as any}
            options={options}
            onChange={onChange}
            inputValue={inputValue}
            onInputChange={onInputChange({
                setInputValue,
                onInputChangeCallback
            })}
            renderOption={renderOption(values)}
            getOptionLabel={getOptionLabel(options)}
            limitTags={limitTags}
            ListboxComponent={List as ComponentType<HTMLAttributes<HTMLElement>>}
            renderInput={renderInput(placeholder, variant as any)}
            disableCloseOnSelect
            disableListWrap
            multiple
        />
    );
}

export const isValueChecked = curry((option: ReactSelectOpt, e: string) => e === option.value);

export const renderOption = curry((values: string[], option: ReactSelectOpt) => {
    const isChecked = Boolean(values.find(isValueChecked(option)));

    return (
        <>
            <Checkbox checked={isChecked} />
            {option.label}
        </>
    );
});

export const findOption = curry((value: StringWithOptionValue, option: ReactSelectOpt) => option.value === value);

export const getOptionLabel = curry((options: ReactSelectOpt[], value: StringWithOptionValue) => {
    if (typeof value === 'string') {
        return options.find(findOption(value))?.label || '';
    }

    return value.label;
});

export const onInputChange = curry((handlers, event, newInputValue) => {
    const {
        onInputChangeCallback,
        setInputValue,
    } = handlers;

    setInputValue(newInputValue);
    onInputChangeCallback(newInputValue);
});

export const renderInput = curry((placeholder: string, variant: string | undefined, params: AutocompleteRenderInputParams) => (
    <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        label={placeholder}
        variant={variant as any}
    />
));

export const getValue = (item: StringWithOptionValue): string => typeof item === 'string' ? item : item.value;
export const filterDuplicate = curry((value: string, id: string) => id !== value);

export const handleValues = (values: StringWithOptionArray) => {
    if (!values.length)
        return [];

    const lastEl = values.pop();

    if (typeof lastEl === 'string')
        return [
            ...values,
            lastEl,
        ];

    const {value} = (lastEl as ReactSelectOpt);

    if ((values as string[]).includes(value))
        return (values as string[]).filter(filterDuplicate(value));

    return [
        ...values,
        value,
    ];
};

export type HandleAutocompleteChange = () => void;

export const handleAutocompleteChange = curry((filterHandlers, _event: BlankChangeEvent, newValues: StringWithOptionArray) => {
    const {
        setFilters,
        setPage,
        setQuery,
        history,
        setQCJobs,
        rowsPerPage,
        filters,
        title,
        sortOrder,
        sortBy,
        route,
        dueDates,
        finishDates,
        key,
    } = filterHandlers;
    const [dateStart, dateEnd] = humanizeRange(dueDates);
    const [finishDateStart, finishDateEnd] = humanizeRange(finishDates);

    const query = {
        ...filters,
        [key]: handleValues(newValues),
        offset: 0,
        title,
        limit: rowsPerPage,
        sortOrder,
        sortBy,
        dateStart,
        dateEnd,
        finishDateStart,
        finishDateEnd
    };

    setPage(0);
    setQuery(history, route, query);
    setFilters(query);
    setQCJobs(query);
});
